import { isPlatformBrowser, Location } from '@angular/common';
import { Inject, inject, Injectable, Optional, PLATFORM_ID, signal } from '@angular/core';
import { NavigationEnd, Params, Router } from '@angular/router';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { AppConfigService } from 'sharedServices/app-config.service';
import {
    setDropOffState,
    dropOffPage,
    submitCallbackRequest,
    start,
} from 'sharedStores/drop-off/drop-off.actions';

import { Route } from 'sharedStores/cms/models/route';
import { DropOffCode } from 'sharedEnums/drop-off-code';
import { RequestQueueSelectors } from 'sharedGlobals/request-queue/request-queue.module';
import { ModalService } from 'sharedGlobals/modals/modal.service';
import * as QuoteActions from 'stores/quote/quote.actions';

import { selectLeadNumber, selectVdn } from 'stores/quote/quote.selectors';
import { ResolveStoreValueService } from 'sharedServices/resolve-store-value.service';
import { QuoteFlowType } from 'sharedEnums/quote-flow-type';
import { selectPolicyHolder } from 'stores/personal/personal.selectors';
import { isNullOrWhitespace } from 'sharedHelpers/utils';
import { DynamicPageData } from 'sharedModels/dynamic-page-data';

import { LoggingService } from 'sharedServices/logging.service';
import { BaseQuoteControllerService } from 'sharedServices/base-quote-controller.service';
import { Events } from 'shared/base/generics/globals/gtm-data-layer/enums/events.enum';
import { DataLayer } from 'shared/base/generics/globals/gtm-data-layer/models/data-layer.model';
import { NavigationService_Token } from 'shared/base/generics/injection-tokens/dependency-tokens';
import { IdNumberFirstNavigationService } from 'services/navigation/id-number-first-navigation.service';
import { SEOService } from 'sharedServices/seo.service';
import { ERROR_CONFIG } from 'shared/base/generics/globals/errors/error-dialog.service';
import { requestFailure } from 'shared/base/generics/store/error/error.actions';
import { acceptableBaseRoutes } from '../constants/quote-flow.constants';
import { DropOffService } from './dropoff.service';

/**
 * Quote Flow Controller
 * - Loads quote flow from CMS
 * - Controls which page to be loaded next (Actual page navigation happens in quote-controller.effects)
 */
@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class QuoteControllerService extends BaseQuoteControllerService {
    public policyHolder = this.store$.selectSignal(selectPolicyHolder);
    public queueIsBusy$ = this.store$.select(RequestQueueSelectors.selectQueueIsBusy);
    public leadNumber = this.store$.selectSignal(selectLeadNumber);
    public vdn = this.store$.selectSignal(selectVdn);
    public navigationService = inject(NavigationService_Token);
    private readonly idNumberFirstNavigation = inject(IdNumberFirstNavigationService);

    public showPremiumBanner = signal(false);
    public hideTrackbar = signal(false);

    constructor(
        public appConfig: AppConfigService,
        public store$: Store,
        public router: Router,
        public dropOffService: DropOffService,
        public modalService: ModalService,
        public seoService: SEOService,
        public location: Location,
        public resolveStoreValueService$: ResolveStoreValueService,
        public loggingService: LoggingService,
        @Inject(PLATFORM_ID) protected readonly platformId: Object,
        @Optional() @Inject(ERROR_CONFIG) errorConfig: any,
    ) {
        super(
            appConfig,
            store$,
            router,
            modalService,
            location,
            loggingService,
            resolveStoreValueService$,
            platformId,
            errorConfig,
        );

        if (this.isPageRefreshed()) {
            this.loggingService.log({
                message: 'Page refreshed',
                level: 'Info',
                tags: ['page-refresh'],
                additionalInfo: [{ name: 'URL', value: this.router.url }],
            });
        }
    }

    private isPageRefreshed(): boolean {
        if (isPlatformBrowser(this.platformId)) {
            const navigationEntries = performance.getEntriesByType(
                'navigation',
            ) as PerformanceNavigationTiming[];
            if (navigationEntries.some((entry) => entry.type === 'reload')) {
                return true;
            }
            if ((performance as any).navigation && (performance as any).navigation.type === 1) {
                return true;
            }
        }
        return false;
    }

    /**
     * Get the starting route based on quote flow type
     * @returns {Route} - The flow type starting route
     */
    public override getFlowTypeStartingRoute(flowType: QuoteFlowType): Route {
        return {
            baseRoute: flowType === QuoteFlowType.HomeContents ? 'home-contents' : 'car-insurance',
            sectionRoute: '',
            pageRoute: '',
        };
    }

    public override checkForDropOffs(): boolean {
        const { isDropOff, pageRoute } = this.dropOffService.checkDropOffPageExist();

        if (isDropOff) {
            this.goToDropOff(pageRoute);
        }
        return isDropOff;
    }

    /**
     * Navigates to the drop-off page.
     *
     * This method handles the navigation to the drop-off page based on the provided route, additional data,
     * and drop-off code. It updates the drop-off state and dispatches the appropriate actions to the store.
     *
     * @param {string} [route=null] - The route to navigate to.
     * @param {any} [additionalData=null] - Optional additional data to include in the drop-off request.
     * @param {DropOffCode} [code=null] - Optional drop-off code to set the drop-off state.
     */
    public override goToDropOff(
        route: string = null,
        additionalData: any = null,
        code: DropOffCode = null,
    ): void {
        if (code) {
            this.store$.dispatch(setDropOffState({ dropOffState: { code } }));
        }

        const dropOffStateCode = code ?? this.dropOffService.dropOffState()?.code;

        this.isDropOff = [
            DropOffCode.UNACCEPTABLE_RISK,
            DropOffCode.REFER_TO_MANAGER,
            DropOffCode.HAS_MULTI_RISK_ITEMS,
            DropOffCode.POLICY_EXISTS,
            DropOffCode.NOT_AUTHORISED_TO_ACCESS_LEAD,
            DropOffCode.SOFT_DELETE,
            DropOffCode.HARD_DELETE,
            DropOffCode.OPERATOR_CODE_UNAVAILABLE,
            DropOffCode.LEAD_BUSY,
            DropOffCode.WALK_AWAY_PRICING_CAMPAIGN,
            DropOffCode.SUSPENDED,
            DropOffCode.UNDER_AGE_RISK,
            DropOffCode.POLICY_CANCELLED,
        ].includes(dropOffStateCode as DropOffCode);

        const isPolicyCancelledSameBrand =
            dropOffStateCode === DropOffCode.REFER_TO_MANAGER &&
            this.dropOffService.dropOffState()?.description === 'Cancelled policy (Same Brand)';

        if (
            this.isDropOff &&
            (isPolicyCancelledSameBrand ||
                (dropOffStateCode !== DropOffCode.REFER_TO_MANAGER &&
                    dropOffStateCode !== DropOffCode.WALK_AWAY_PRICING_CAMPAIGN))
        ) {
            this.store$.dispatch(dropOffPage({ route, additionalData }));
            this.router.navigate(['callback'], { queryParamsHandling: 'preserve' });
            return;
        }

        const hasCallbackPersonalDetails =
            !isNullOrWhitespace(this.policyHolder().person?.firstName) &&
            !isNullOrWhitespace(this.policyHolder().person?.lastName) &&
            !isNullOrWhitespace(this.policyHolder().person?.phone);

        if (!route || !hasCallbackPersonalDetails) {
            this.store$.dispatch(start({ route }));
        } else {
            this.store$.dispatch(
                submitCallbackRequest({
                    firstName: this.policyHolder().person?.firstName,
                    surname: this.policyHolder().person?.lastName,
                    contactNumber: this.policyHolder().person?.phone,
                    route,
                    callbackReason: 'AVS Attempt Limit reached and chose to request a callback.',
                    additionalData,
                }),
            );
        }
        this.router.navigate(['callback'], { queryParamsHandling: 'preserve' });
    }

    public override setQueryParams(queryParams?: Params) {
        if (!queryParams) {
            // Fallback to router's snapshot if queryParams not provided
            const snapshot = this.router.routerState.snapshot.root;
            queryParams = snapshot?.queryParams || {};
        }

        this.store$.dispatch(QuoteActions.setQueryParams({ queryParams }));
    }

    public override setHideTrackBar(isPageLoading: boolean) {
        this.hideTrackbar.set(isPageLoading);
    }

    public override setShowPremiumBanner(isPageLoading: boolean) {
        this.showPremiumBanner.set(!isPageLoading);
    }

    public override setQuoteFlowType(page: DynamicPageData) {
        // Determine which baseRoute to use for setting the quote flow type:
        // If the baseRoute in page.route is in the acceptable set, use it.
        // Else, if page.page.pageBase exists (for S&R cases), use its baseRoute.
        const quoteFlowType = acceptableBaseRoutes.has(page.route.baseRoute as QuoteFlowType)
            ? (page?.route.baseRoute as QuoteFlowType)
            : (page?.page?.pageBase?.baseRoute as QuoteFlowType);

        if (quoteFlowType) {
            this.store$.dispatch(QuoteActions.setQuoteFlowType({ quoteFlowType }));
        }
    }

    /**
     * Processes the router event.
     *
     * This methods submits page navigation meta data to the data layer after each page navigation event.
     *
     * @param {NavigationEnd} event - The navigation end event containing the URL after redirects.
     */
    public override submitPageNavigationToDataLayer(event: NavigationEnd): void {
        if (!isPlatformBrowser(this.platformId)) {
            // Don't run DataLayer logic on the server
            return;
        }
        const dataLayer = new DataLayer({
            event: Events.PAGE_META_DATA,
            page: {
                type: this.activePage()?.page?.route,
                title: document.title,
                brand: this.appConfig.config()?.brandDescription,
                environment: this.appConfig.config()?.production ? 'Live' : 'Pre-Prod',
                path: document.location?.origin + event.urlAfterRedirects,
                vdn: this.vdn(),
            },
            user: {
                loggedIn: false,
            },
        });

        const layerExists = dataLayer
            .getLayers()
            .findIndex(
                (layer) =>
                    layer.event === Events.PAGE_META_DATA &&
                    layer.page.type === this.activePage()?.page?.route,
            );

        if (layerExists < 0) {
            dataLayer.submit();
        }
    }

    protected createCanonicalLink(): void {
        this.seoService.createLinkForCanonicalURL();
    }

    /**
     * Override back navigation for the ID-first variant flow.
     *
     * Uses IdNumberFirstNavigationService to make intelligent decisions based on CMS state.
     * Only overrides when necessary to break loops or route to specific pages.
     * For all other cases, delegates to normal browser back navigation.
     */
    public override back(): void {
        const { pageRoute, sectionRoute } = this.getCurrentRoutes();

        // Check if we need to override back navigation for the variant
        const overrideRoute = this.idNumberFirstNavigation.getBackNavigationOverride(
            pageRoute,
            sectionRoute,
        );
        if (overrideRoute) {
            this.navigate(overrideRoute);
            return;
        }

        // Suppress id-page auto-skip only when in the variant — non-variant users have no
        // skipPageEffect, so calling this for them interferes with normal location.back().
        if (this.idNumberFirstNavigation.isIdFirstVariant()) {
            this.idNumberFirstNavigation.suppressIdPageSkipForBackNavigation(
                pageRoute,
                sectionRoute,
            );
        }

        // Use normal browser back navigation
        super.back();
    }

    /**
     * Extracts the current page and section routes from the router snapshot.
     * Uses routerState.snapshot (ActivatedRouteSnapshot) so that `data` is the
     * resolved plain object, not the Observable<Data> on ActivatedRoute.
     */
    private getCurrentRoutes(): { pageRoute: string; sectionRoute: string } {
        let pageRoute = '';
        let sectionRoute = '';

        const extractRoutes = (snapshot: any) => {
            if (!snapshot) return;

            if (snapshot.data) {
                pageRoute = snapshot.data['pageRoute'] || pageRoute;
                sectionRoute = snapshot.data['sectionRoute'] || sectionRoute;
            }

            if (snapshot.firstChild) {
                extractRoutes(snapshot.firstChild);
            }
        };

        extractRoutes(this.router.routerState.snapshot.root);
        return { pageRoute, sectionRoute };
    }

    protected dispatchRequestFailure(payload: {
        isErrored: boolean;
        message: string;
        status: number;
    }): void {
        this.store$.dispatch(
            requestFailure({ isErrored: true, message: payload.message, status: payload.status }),
        );
    }
}
